import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Clock, X } from "lucide-react";
import useSubscriptionStore from "../../store/subscription.store";

const SNOOZE_KEY = "zcd_trial_banner_snoozed_until";

// Persistent header banner for users on the 30-day trial (pricing v2).
//
// Purely presentational. Does NOT call startTrial — the trial is granted
// at signup-time inside RegisterSerializer. The earlier "auto-fire
// startTrial on mount when no active sub" pattern was a free-credit
// faucet (TrialBanner mounts on every layout switch; the no-dedup window
// let one user accumulate 38 grants × 100 ZC). Backend now enforces a
// lifetime one-shot per user; frontend stays out of granting territory.
export default function TrialBanner() {
  const mySubscription = useSubscriptionStore((s) => s.mySubscription);

  // Snooze — dismiss for 4 hours so users on day 2/3 aren't nagged on
  // every page load. Critical (< 24h) and urgent (< 48h) tiers ignore
  // the snooze so we still alert before the cliff.
  const [snoozedUntil, setSnoozedUntil] = useState(() => {
    try {
      const raw = localStorage.getItem(SNOOZE_KEY);
      return raw ? parseInt(raw, 10) : 0;
    } catch { return 0; }
  });
  // Re-evaluate when the page returns from background.
  useEffect(() => {
    const onVis = () => {
      try {
        const raw = localStorage.getItem(SNOOZE_KEY);
        setSnoozedUntil(raw ? parseInt(raw, 10) : 0);
      } catch { /* ignore */ }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  if (!mySubscription?.is_trial || !mySubscription?.trial_ends_at) return null;

  const ends = new Date(mySubscription.trial_ends_at);
  const now = new Date();
  const msLeft = ends - now;
  if (msLeft <= 0) return null; // expired sub modal handles this

  const hoursLeft = msLeft / 3_600_000;
  const daysLeft = Math.floor(hoursLeft / 24);
  const remainingLabel =
    daysLeft >= 1
      ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`
      : `${Math.max(1, Math.floor(hoursLeft))} hour${Math.floor(hoursLeft) === 1 ? "" : "s"} left`;

  // Tone + copy shift as the trial winds down — gentle then urgent.
  const urgent = hoursLeft < 48;
  const critical = hoursLeft < 24;

  // Honour snooze only for the gentle tier — never silence the urgent /
  // critical bands.
  const isSnoozed = !urgent && !critical && Date.now() < snoozedUntil;
  if (isSnoozed) return null;

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-2 text-sm ${
      critical
        ? "bg-rose-500/20 text-rose-100 ring-1 ring-rose-500/40"
        : urgent
          ? "bg-amber-500/15 text-amber-200"
          : "bg-button-primary/10 text-text-primary"
    }`}>
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles className="w-4 h-4 shrink-0" />
        <span className="truncate">
          {critical ? (
            <>
              <strong>Trial ends in {remainingLabel.replace(" left", "")}</strong> —
              pick a plan to keep scoring.
            </>
          ) : urgent ? (
            <>
              <strong>Trial ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}</strong> —
              pick a plan before features lock.
            </>
          ) : (
            <>
              You're on the <strong>30-day trial</strong>. 100 credits ≈
              {" "}<span title="2 ZC each">50 chat messages</span> or
              {" "}<span title="7 ZC each">14 AI Suggests</span> or
              {" "}<span title="15 ZC each">6 auto-tag runs</span>.
            </>
          )}
        </span>
        <span className="hidden sm:inline-flex items-center gap-1 ml-2 opacity-90">
          <Clock className="w-3.5 h-3.5" />
          {remainingLabel}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/subscriptions"
          className={`text-xs font-semibold px-3 py-1 rounded hover:opacity-90 ${
            critical
              ? "bg-rose-500 text-white animate-pulse"
              : urgent
                ? "bg-amber-500 text-bg-primary"
                : "bg-button-primary text-white"
          }`}
        >
          {urgent ? "Pick a plan →" : "Upgrade"}
        </Link>
        {!urgent && !critical && (
          <button
            onClick={() => {
              const until = Date.now() + 4 * 60 * 60 * 1000;
              try { localStorage.setItem(SNOOZE_KEY, String(until)); } catch { /* ignore */ }
              setSnoozedUntil(until);
            }}
            className="text-text-secondary hover:text-text-primary p-1"
            title="Snooze for 4 hours (banner auto-returns once <48h remain)"
            aria-label="Snooze trial banner"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
